<script setup lang="ts">
/**
 * CoreForm
 *
 * Optional `<form>` wrapper that:
 * - tracks "submitted" state for fields (to show errors after submit)
 * - collects registered fields (optional) and can validate them all
 * - emits `submit` only if validation passes (unless disabled via prop)
 *
 * Field registration is optional: `Core*Field` components work standalone.
 */
import { computed, provide, ref } from 'vue'
import type { CoreFormContext, CoreFormFieldHandle } from './keys'
import { coreFormKey } from './keys'

const props = withDefaults(
  defineProps<{
    disableValidation?: boolean
  }>(),
  {
    disableValidation: false,
  },
)

const emit = defineEmits<{
  (e: 'submit', event: SubmitEvent): void
}>()

const submitted = ref(false)
const fields = ref(new Map<string, CoreFormFieldHandle>())

const registerField: CoreFormContext['registerField'] = (handle) => {
  fields.value.set(handle.key, handle)
  return () => {
    const existing = fields.value.get(handle.key)
    if (existing === handle) fields.value.delete(handle.key)
  }
}

provide(coreFormKey, { submitted, registerField })

const validate = async () => {
  const errors: Record<string, string[]> = {}
  let valid = true

  const handles = Array.from(fields.value.values())
  for (const handle of handles) {
    const fieldErrors = await handle.validate()
    if (fieldErrors.length) {
      valid = false
      errors[handle.key] = fieldErrors
    }
  }

  return { valid, errors }
}

const resetValidation = () => {
  submitted.value = false
  fields.value.forEach((handle) => handle.resetValidation())
}

const shouldValidate = computed(() => !props.disableValidation)

const onSubmit = async (event: SubmitEvent) => {
  submitted.value = true

  if (!shouldValidate.value) {
    emit('submit', event)
    return
  }

  const result = await validate()
  if (!result.valid) return
  emit('submit', event)
}

defineExpose({ validate, resetValidation })
</script>

<template>
  <form @submit.prevent="onSubmit">
    <slot />
  </form>
</template>
